<template>
  <button
    ref="buttonRef"
    v-bind="$attrs"
    type="button"
    class="help-tip text-surface-400 hover:text-surface-700 focus-visible:text-surface-700 inline-flex shrink-0 items-center justify-center rounded-full align-middle transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:[--tw-ring-color:var(--stw-glass-ring-focus)]"
    :aria-label="`Help: ${help.title}`"
    :aria-expanded="isOpen"
    @click.stop.prevent="handleClick"
    @pointerdown.stop
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @focus="handleFocus"
    @blur="close"
  >
    <i class="pi pi-question-circle text-sm" />
  </button>

  <Popover
    ref="popoverRef"
    :pt="{
      root: 'max-w-[min(20rem,calc(100vw-2rem))]',
      content: 'p-3 text-sm',
    }"
    @show="isOpen = true"
    @hide="isOpen = false"
  >
    <div class="space-y-3">
      <div v-for="section in sections" :key="section.title">
        <div class="font-semibold">{{ section.title }}</div>
        <div class="mt-1 space-y-2 opacity-80">
          <template v-for="(block, b) in section.blocks" :key="b">
            <p v-if="block.type === 'paragraph'">
              <template v-for="(line, l) in block.lines" :key="l">
                <br v-if="l > 0" />
                <span
                  v-for="(span, i) in line"
                  :key="i"
                  :class="{
                    'font-semibold': span.bold,
                    italic: span.italic,
                  }"
                >
                  {{ span.text }}
                </span>
              </template>
            </p>
            <ul v-else class="list-disc pl-5">
              <li v-for="(line, l) in block.items" :key="l">
                <span
                  v-for="(span, i) in line"
                  :key="i"
                  :class="{
                    'font-semibold': span.bold,
                    italic: span.italic,
                  }"
                >
                  {{ span.text }}
                </span>
              </li>
            </ul>
          </template>
        </div>
      </div>
    </div>
  </Popover>
</template>

<script setup lang="ts">
import { Popover } from 'primevue';
import { computed, onBeforeUnmount, ref } from 'vue';

import { HELP_TEXTS, type HelpTextKey } from '@/constants/helpTexts';
import { parseHelpText } from '@/utils/helpText';

/**
 * Small (?) icon that explains a feature. Hover opens it on PC; tap toggles
 * it on touch screens (tap outside closes). The text lives in
 * constants/helpTexts.ts.
 */
interface Props {
  helpKey: HelpTextKey;
}

const props = defineProps<Props>();

// Two root nodes (button + popover): pass class etc. to the button
defineOptions({ inheritAttrs: false });

const help = computed(() => HELP_TEXTS[props.helpKey]);

// One section, or several when the icon explains more than one thing
const sections = computed(() => {
  const entry = help.value;
  const list =
    'sections' in entry
      ? entry.sections
      : [{ title: entry.title, text: entry.text }];
  return list.map((s) => ({ title: s.title, blocks: parseHelpText(s.text) }));
});

const buttonRef = ref<HTMLButtonElement>();
const popoverRef = ref<InstanceType<typeof Popover>>();
const isOpen = ref(false);

// Touch screens have no hover: detect the input, not the screen width
const canHover = () => window.matchMedia('(hover: hover)').matches;

let closeTimer: ReturnType<typeof setTimeout> | undefined;

const open = (event: Event) => {
  clearTimeout(closeTimer);
  if (!isOpen.value) popoverRef.value?.show(event, buttonRef.value);
};

const close = () => {
  clearTimeout(closeTimer);
  popoverRef.value?.hide();
};

const handleMouseEnter = (event: MouseEvent) => {
  if (canHover()) open(event);
};

// Short delay so moving the mouse across the icon's edge doesn't flicker
const handleMouseLeave = () => {
  if (!canHover()) return;
  clearTimeout(closeTimer);
  closeTimer = setTimeout(close, 100);
};

const handleClick = (event: MouseEvent) => {
  if (canHover()) return; // already open from hover
  if (isOpen.value) close();
  else open(event);
};

// Keyboard: open on focus. Skip focus from a tap, which the click handles.
const handleFocus = (event: FocusEvent) => {
  if (canHover() && buttonRef.value?.matches(':focus-visible')) open(event);
};

onBeforeUnmount(() => clearTimeout(closeTimer));
</script>

<style scoped>
/* 32px tap target on touch screens, without making the icon bigger */
.help-tip {
  width: 1.25rem;
  height: 1.25rem;
}

@media (hover: none) {
  .help-tip {
    width: 2rem;
    height: 2rem;
  }
}
</style>
